import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Wrench, Plus, UserCog } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useRealtime } from "@/context/RealtimeContext";
import StatusBadge from "@/components/StatusBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const STAFF = ["super_admin", "owner", "store_manager", "staff"];
const STATUSES = ["New", "Assigned", "In progress", "Completed", "Cancelled"];
const TECH_STATUSES = ["In progress", "Completed", "Cancelled"];

export default function AdminServiceJobs() {
  const { user } = useAuth();
  const isTech = user?.role === "technician";
  const isStaff = STAFF.includes(user?.role);
  const { subscribe } = useRealtime();
  const [jobs, setJobs] = useState([]);
  const [techs, setTechs] = useState([]);
  const [types, setTypes] = useState([]);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ service_type: "", customer_name: "", phone: "", description: "", address: "" });

  const load = useCallback(() => {
    const url = isTech ? "/technician/requests" : "/admin/service-requests";
    api.get(url).then((r) => setJobs(r.data)).catch(() => {});
  }, [isTech]);
  useEffect(() => { if (user) load(); }, [user, load]);
  useEffect(() => {
    if (isStaff) { api.get("/admin/technicians").then((r) => setTechs(r.data)).catch(() => {}); api.get("/service-types").then((r) => setTypes(r.data)).catch(() => {}); }
  }, [isStaff]);
  useEffect(() => subscribe((m) => { if (m.event?.startsWith("service")) load(); }), [subscribe, load]);

  const assign = async (job, technician_id) => {
    if (!technician_id) return;
    try { await api.put(`/admin/service-requests/${job.id}/assign`, { technician_id }); toast.success("Technician assigned"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const setStatus = async (job, status) => {
    const url = isTech ? `/technician/requests/${job.id}/status` : `/admin/service-requests/${job.id}/status`;
    try { await api.put(url, { status }); toast.success(`Status: ${status}`); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const createJob = async () => {
    if (!form.service_type) return toast.error("Service type chunein");
    try { await api.post("/admin/service-requests", form); toast.success("Service job created"); setModal(false); setForm({ service_type: "", customer_name: "", phone: "", description: "", address: "" }); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-5" data-testid="service-jobs-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold flex items-center gap-2"><Wrench className="text-orange-500" /> {isTech ? "My Service Jobs" : "Service Jobs"}</h1>
        {isStaff && <button data-testid="new-service-job" onClick={() => setModal(true)} className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-bold text-sm flex items-center gap-2"><Plus size={16} /> New Job</button>}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
        <table className="w-full text-sm min-w-[820px]">
          <thead><tr className="text-xs font-bold uppercase tracking-wider text-slate-500 bg-slate-50">
            <th className="text-left py-3 px-4">Request</th><th className="text-left py-3 px-4">Service</th><th className="text-left py-3 px-4">Customer</th><th className="text-left py-3 px-4">Technician</th><th className="text-left py-3 px-4">Status</th><th className="text-right py-3 px-4">Action</th></tr></thead>
          <tbody>{jobs.map((j) => (
            <tr key={j.id} data-testid={`service-job-${j.id}`} className="border-b border-slate-50 hover:bg-slate-50 align-top">
              <td className="py-2.5 px-4"><p className="font-semibold">{j.request_no}</p><p className="text-xs text-slate-400">{new Date(j.created_at).toLocaleDateString("en-IN")}</p></td>
              <td className="py-2.5 px-4"><p className="font-medium">{j.service_type}</p>{j.description && <p className="text-xs text-slate-400 max-w-[200px] line-clamp-2">{j.description}</p>}</td>
              <td className="py-2.5 px-4">{j.customer_name || "—"}<br/><span className="text-xs text-slate-400">{j.customer_phone || ""}</span></td>
              <td className="py-2.5 px-4">
                {isStaff ? (
                  <select data-testid={`assign-${j.id}`} value={j.technician_id || ""} onChange={(e) => assign(j, e.target.value)} className="border rounded px-2 py-1 text-xs">
                    <option value="">— assign —</option>
                    {techs.map((t) => <option key={t.id} value={t.id}>{t.name || t.email}</option>)}
                  </select>
                ) : (j.technician_name || "—")}
              </td>
              <td className="py-2.5 px-4"><StatusBadge status={j.status} testid={`service-status-${j.id}`} /></td>
              <td className="py-2.5 px-4 text-right">
                <div className="flex flex-wrap gap-1 justify-end">
                  {(isTech ? TECH_STATUSES : STATUSES).map((s) => (
                    <button key={s} data-testid={`svc-set-${s.replace(/ /g, "-")}-${j.id}`} onClick={() => setStatus(j, s)} className={`text-[11px] px-2 py-1 rounded border ${j.status === s ? "bg-orange-500 text-white border-orange-500" : "bg-white border-slate-200 hover:bg-slate-50"}`}>{s}</button>
                  ))}
                </div>
              </td>
            </tr>
          ))}
          {!jobs.length && <tr><td colSpan="6" className="text-center py-12 text-slate-400"><UserCog size={28} className="mx-auto opacity-30 mb-2" />{isTech ? "Abhi koi job assign nahi hui." : "Koi service request nahi."}</td></tr>}</tbody>
        </table>
      </div>

      <Dialog open={modal} onOpenChange={setModal}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle>New Service Job</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-1">
            <select data-testid="sj-type" value={form.service_type} onChange={(e) => setForm({ ...form, service_type: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm">
              <option value="">Service type…</option>
              {types.map((t) => <option key={t.id} value={t.name}>{t.name}</option>)}
            </select>
            <div className="grid grid-cols-2 gap-2">
              <input data-testid="sj-cust" placeholder="Customer name" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} className="border rounded-lg px-3 py-2 text-sm" />
              <input data-testid="sj-phone" placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="border rounded-lg px-3 py-2 text-sm" />
            </div>
            <input data-testid="sj-address" placeholder="Address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm" />
            <textarea data-testid="sj-desc" placeholder="Problem / description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm h-16" />
            <button data-testid="sj-save" onClick={createJob} className="w-full py-2.5 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-bold">Create Job</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
